import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';
import cn from '@/utils/cn';
import compactSelectStyles from '../../shared/compactSelectStyles';
import { Field, PanelFooter, hintClass, panelClass } from '../../shared/formPanel';
import { IMAGES_HINT, ImageRow } from '../../shared/ImageRow';
import SPASectionHeading from '../../shared/SPASectionHeading';
import SPASelect from '../../shared/SPASelect';
import { useSPAToast } from '../../shared/SPAToast';
import { EditableUnit, useUpdateUnit } from './useUnit';

// Program::BACKGROUND_POSITIONS
const POSITION_OPTIONS = ['top', 'center', 'bottom'].map(value => ({ label: value, value }));

interface FormValues {
  student_text_image_background_position: string;
}

const UnitImagePanel = () => {
  const unit = useOutletContext<EditableUnit>();
  const { programId } = useParams<{ programId: string }>();
  const spaToast = useSPAToast();
  const updateUnit = useUpdateUnit(Number(programId), unit.id);
  const [pending, setPending] = useState<File | undefined>();
  const { control, handleSubmit } = useForm<FormValues>({
    values: { student_text_image_background_position: unit.student_text_image_background_position || 'center' },
  });

  const save = (values: FormValues) => {
    updateUnit.mutate({ unit: values, uploads: { student_text_image: pending } }, {
      onSuccess: () => {
        setPending(undefined);
        spaToast.success('Saved unit image.');
      },
    });
  };

  return (
    <form noValidate className={panelClass} onSubmit={handleSubmit(save)}>
      <SPASectionHeading className="tw-mb-3" title="Unit Image" />
      <span className={cn(hintClass, 'tw-mb-3 tw-mt-0')}>{IMAGES_HINT}</span>

      <ImageRow
        image={{
          file_name: unit.student_text_image_file_name,
          thumb_url: unit.student_text_image_thumb_url,
          url: unit.poster_url,
        }}
        inputId="unit_student_text_image"
        label="Student Text Image"
        pending={pending}
        onPick={setPending}
      />

      <Field
        className="tw-mt-4 tw-w-48"
        hint="Where the image anchors when it is cropped."
        htmlFor="unit_student_text_image_background_position"
        label="Background Position"
      >
        <Controller
          control={control}
          name="student_text_image_background_position"
          render={({ field }) => (
            <SPASelect
              inputId="unit_student_text_image_background_position"
              options={POSITION_OPTIONS}
              styles={compactSelectStyles}
              value={POSITION_OPTIONS.find(option => option.value === field.value) ?? null}
              onChange={option => field.onChange(option?.value ?? 'center')}
            />
          )}
        />
      </Field>

      <PanelFooter update={updateUnit} />
    </form>
  );
};

export default UnitImagePanel;
